/* =========================================================================
 * IBC 4.1 — FULFILMENT PICKER
 * -------------------------------------------------------------------------
 * Delivery or collection, chosen in the basket. Drives the picker on the
 * cart page and in the drawer from one controller, because both surfaces
 * render snippets/ibc-fulfilment.liquid.
 *
 * FIVE RULES THIS FILE OBEYS
 * --------------------------
 * 1. THE CART IS THE STATE. Not this file. Every choice is written to a cart
 *    attribute, and `snippets/ibc-fulfilment-state.liquid` reads those
 *    attributes back. The module-level `state` below is a CACHE of what was
 *    last written, kept so the two surfaces can be updated without a round
 *    trip — never a second source of truth.
 *
 * 2. NOTHING IS BOUND TO AN ELEMENT. `assets/ibc-cart.js` replaces the whole
 *    contents of `[data-ibc-cart-refresh]` after every basket mutation, so
 *    any reference held here would point at a detached node within seconds.
 *    Every handler is delegated from `document` and re-reads the DOM.
 *
 * 3. BOTH SURFACES MOVE TOGETHER. On /cart the page picker and the drawer
 *    picker are both in the document, showing the same basket. A change to
 *    one is applied to every root, so they cannot disagree.
 *
 * 4. DATES ARE ANCHORED AT MIDDAY. Every Date built here is constructed at
 *    12:00 local. A date built at midnight and then shifted across a clock
 *    change lands on the previous day, which is the classic way a "Saturday"
 *    booking becomes a Friday one twice a year.
 *
 * 5. FAIL VISIBLY, NOT SILENTLY. If a write fails, the choice is rolled back
 *    in the UI and the cart's own error slot is used. A picker that shows a
 *    selection the cart does not have is worse than one that shows none.
 *
 * THE SEAM FOR REAL AVAILABILITY
 * ------------------------------
 * `slotsFor()` and `dayState()` are the only two functions that decide what
 * is offerable. The Pickup Scheduler app already answers both server-side at
 * /apps/ibc-pickup/availability, and when that is wired in, those two
 * functions become a fetch and a cache and NOTHING ELSE IN THIS FILE MOVES.
 * That is why they take a date and return a verdict rather than reaching
 * into the DOM.
 *
 * Depends on: nothing. Same ES5 dialect as assets/ibc-cart.js — the theme
 * ships no transpiler and this file has to run wherever that one does.
 * ====================================================================== */
(function () {
  'use strict';

  /* RE-ENTRY GUARD. Both cart sections emit this file, so on /cart the
   * browser executes it twice — one fetch, two <script> tags, two runs. The
   * second run would register a second document-level click listener and
   * every day cell would be selected, then immediately re-selected. Same
   * idiom, and same reason, as assets/ibc-cart.js. */
  if (window.IbcFulfilment) return;

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /* "Sept", not "Sep". It is what the shop's existing pickup labels already
   * say, and a basket that spells the month two ways looks broken. */
  var MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
  ];

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  var MSG = {
    failed: 'Sorry, that could not be saved. Please try again.',
    gate: 'Choose delivery or collection above to continue.'
  };

  /* ==================================================================== */
  /* SMALL UTILITIES                                                      */
  /* ==================================================================== */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function $all(selector, scope) {
    return toArray((scope || document).querySelectorAll(selector));
  }

  function $one(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function closest(node, selector) {
    while (node && node.nodeType === 1) {
      if (node.matches ? node.matches(selector) : false) return node;
      node = node.parentNode;
    }
    return null;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* ==================================================================== */
  /* DATES                                                                */
  /* ==================================================================== */

  /* 'YYYY-MM-DD' -> Date at 12:00 LOCAL. See rule 4 above. Returns null for
   * anything that is not exactly that shape, because a half-parsed date is
   * how a picker ends up offering the 1st of January 1970. */
  function parseISO(value) {
    if (typeof value !== 'string' || value.length !== 10) return null;
    var y = Number(value.slice(0, 4));
    var m = Number(value.slice(5, 7));
    var d = Number(value.slice(8, 10));
    if (!y || !m || !d || value.charAt(4) !== '-' || value.charAt(7) !== '-') return null;
    var date = new Date(y, m - 1, d, 12, 0, 0, 0);
    /* Rejects 31 February rather than silently rolling it into March. */
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
    return date;
  }

  function toISO(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function addDays(date, n) {
    var next = new Date(date.getTime());
    next.setDate(next.getDate() + n);
    next.setHours(12, 0, 0, 0);
    return next;
  }

  /* THERE IS NO addWorkingDays ANY MORE, and its absence is the point.
   *
   * The delivery lead time is CALENDAR days: it is how long the shop needs to
   * make the order and hand it to the courier, and the courier being shut on
   * a Monday does not make a cake take longer to bake. So the lead is a plain
   * day count — see minDate — and the closed weekdays do their work once, in
   * dayState, by shading the days a courier will not deliver on.
   *
   * Counting the lead in delivering days charged every closed day twice. On a
   * Saturday with Sunday and Monday closed and two days' lead, it offered
   * Wednesday: the Sunday and Monday were skipped by the count AND unavailable
   * to land on. The shop can ship that Monday for a Tuesday arrival, so
   * Tuesday is the honest answer.
   *
   * app/lib/delivery.js earliestDeliveryDate() is the same rule, in one
   * function rather than two, because the admin preview has to promise exactly
   * what the basket will offer. Change one, change the other. */

  function ordinal(n) {
    var rem = n % 100;
    if (rem >= 11 && rem <= 13) return n + 'th';
    if (n % 10 === 1) return n + 'st';
    if (n % 10 === 2) return n + 'nd';
    if (n % 10 === 3) return n + 'rd';
    return n + 'th';
  }

  /* 'Sunday 20 September' — the shop's existing pickup spelling. */
  function labelLong(date) {
    return DAYS[date.getDay()] + ' ' + date.getDate() + ' ' + MONTHS[date.getMonth()];
  }

  /* 'Sat 20th Sept' — short enough to sit inside a renamed delivery rate at
   * checkout without wrapping, and inside the function's 40-character cap. */
  function labelShort(date) {
    return DAYS_SHORT[date.getDay()] + ' ' + ordinal(date.getDate()) + ' ' + MONTHS_SHORT[date.getMonth()];
  }

  /* 'Saturday 20 September 2026' — the accessible name of a day cell. The
   * year is included because the grid pages past a year boundary. */
  function labelCell(date) {
    return labelLong(date) + ' ' + date.getFullYear();
  }

  function minutesToClock(mins) {
    var h24 = Math.floor(mins / 60) % 24;
    var m = mins % 60;
    var h = h24 % 12;
    if (h === 0) h = 12;
    return { text: h + ':' + pad(m), suffix: h24 < 12 ? 'am' : 'pm', h24: h24, m: m };
  }

  /* '1:00–3:00pm', and '11:30am–12:00pm' when the range straddles noon.
   * One suffix when both ends share it, two when they do not — the same rule
   * the collection slots use, so the two never spell a range differently in
   * the same drawer. */
  function rangeLabel(start, end) {
    var a = minutesToClock(start);
    var b = minutesToClock(end);
    return a.suffix === b.suffix
      ? a.text + '–' + b.text + b.suffix
      : a.text + a.suffix + '–' + b.text + b.suffix;
  }

  function clockToMinutes(value) {
    var bits = String(value || '').split(':');
    var h = Number(bits[0]);
    var m = Number(bits[1]);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  /* '+0100' -> '+01:00'. Shopify's `%z` has no colon; ISO 8601 wants one and
   * so does every parser that will later read the order. */
  function tzColon(raw) {
    var value = String(raw || '').trim();
    if (/^[+-]\d{4}$/.test(value)) return value.slice(0, 3) + ':' + value.slice(3);
    if (/^[+-]\d{2}:\d{2}$/.test(value)) return value;
    return '+00:00';
  }

  /* ==================================================================== */
  /* CONFIGURATION, READ OFF THE ROOT                                     */
  /* ==================================================================== */

  function numAttr(root, name, fallback) {
    var raw = Number(root.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function listAttr(root, name) {
    return String(root.getAttribute(name) || '')
      .split(',')
      .map(function (part) { return part.trim(); })
      .filter(function (part) { return part.length > 0; });
  }

  /* OPENING HOURS, ONE ENTRY PER WEEKDAY, Sunday first — the order
   * `Date.getDay()` returns, so the weekday indexes the array directly.
   *
   * 'start-end' for a day the shop is open, empty for a day it is shut. An
   * empty segment is a CLOSED DAY, not a missing one: position carries the
   * weekday, so a shut Monday cannot be dropped without shifting Tuesday
   * onto Monday's hours. */
  function hoursAttr(root) {
    var raw = String(root.getAttribute('data-hours') || '').split(',');
    var out = [];
    for (var i = 0; i < 7; i++) {
      var bits = String(raw[i] || '').split('-');
      var from = clockToMinutes(bits[0]);
      var to = clockToMinutes(bits[1]);
      out.push(from === null || to === null || to <= from ? null : { from: from, to: to });
    }
    return out;
  }

  function conf(root) {
    return {
      today: parseISO(root.getAttribute('data-today')) || new Date(),
      tz: tzColon(root.getAttribute('data-tz')),
      deliveryLead: numAttr(root, 'data-delivery-lead', 2),
      pickupLead: numAttr(root, 'data-pickup-lead', 1),
      window: numAttr(root, 'data-window', 60),
      deliveryWindow: numAttr(root, 'data-delivery-window', 60),
      pickupClosedDays: listAttr(root, 'data-pickup-closed-days').map(Number),
      deliveryClosedDays: listAttr(root, 'data-delivery-closed-days').map(Number),
      closedDates: listAttr(root, 'data-closed-dates'),
      deliveryClosedDates: listAttr(root, 'data-delivery-closed-dates'),
      hours: hoursAttr(root),
      slot: numAttr(root, 'data-slot', 30),
      sameDay: root.getAttribute('data-same-day') === '1',
      cutoff: clockToMinutes(root.getAttribute('data-cutoff')),
      deliveryCutoff: clockToMinutes(root.getAttribute('data-delivery-cutoff')),
      slotCap: numAttr(root, 'data-slot-cap', 0),
      /* PREPARATION MINUTES THE BASKET NEEDS before it can be collected —
       * the largest custom.pickup_delay_minutes among the products in it,
       * floored at 60. Computed in Liquid because only the server can see
       * the line items' metafields. Whole cakes carry 180. */
      prepMinutes: numAttr(root, 'data-prep-minutes', 60),
      /* Whether each method is even rendered. Read so the controller can
       * stop looking for a tab that is not there rather than throwing. */
      pickupOffered: root.getAttribute('data-pickup-offered') !== '0',
      deliveryOffered: root.getAttribute('data-delivery-offered') !== '0',
      rulesSource: root.getAttribute('data-rules-source') || 'builtin',
      location: root.getAttribute('data-location') || '',
      /* SAME-DAY COURIER. `samedayOn` false is the normal state: the tile is
       * not rendered at all unless the app switched it on, so everything
       * below is dead weight in almost every basket and costs nothing. */
      samedayOn: root.getAttribute('data-sameday') === '1',
      samedayCutoff: clockToMinutes(root.getAttribute('data-sameday-cutoff')),
      samedayEnd: clockToMinutes(root.getAttribute('data-sameday-end')),
      samedayLead: numAttr(root, 'data-sameday-lead', 120),
      samedayWindow: numAttr(root, 'data-sameday-window', 120),
      samedayMinWindow: numAttr(root, 'data-sameday-min-window', 45),
      samedayOpen: root.getAttribute('data-sameday-open') === '1',
      samedayOpenLabel: root.getAttribute('data-sameday-open-label') || 'Any time before 9pm',
      samedayOut: root.getAttribute('data-sameday-out') || '',
      /* WHERE LIVE PRICES COME FROM.
       *
       * An app-proxy path, so it is same-origin: no CORS, no second domain
       * in the storefront's network tab, and Shopify signs the request on
       * the way through. The attribute exists so the prefix can move
       * without a theme deploy. */
      quoteUrl: root.getAttribute('data-sameday-quote') || '/apps/ibc-pickup/quote',
      zones: zonesFor(root)
    };
  }

  /* ==================================================================== */
  /* SAME-DAY COURIER                                                     */
  /* ==================================================================== */

  /* THE ZONES, PARSED ONCE PER ROOT AND CACHED. Liquid writes them into a
   * JSON script tag rather than an attribute because ninety-nine outward
   * codes is a long way past what belongs in one.
   *
   * A malformed blob yields an empty list, which makes every postcode read
   * as out of area. That is the safe direction: refusing a delivery we
   * could have made is recoverable, promising one we cannot is not. */
  var zoneCache = null;

  function zonesFor(root) {
    if (zoneCache) return zoneCache;
    var node = $one('[data-ibc-fulfil-zones]', root);
    if (!node) { zoneCache = []; return zoneCache; }
    try {
      var parsed = JSON.parse(node.textContent || '[]');
      zoneCache = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      zoneCache = [];
    }
    return zoneCache;
  }

  /* UK postcodes are "outward inward" and the inward is always three
   * characters, so the outward is everything but the last three. Splitting
   * on the space is unreliable: people leave it out.
   *
   * MIRRORS outwardCode() in app/lib/sameday.js. If you change one, change
   * the other — a Function and a theme cannot share a file. */
  function outwardCode(value) {
    if (typeof value !== 'string') return null;
    var cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length < 5 || cleaned.length > 7) return null;
    var outward = cleaned.slice(0, cleaned.length - 3);
    return /^[A-Z]{1,2}[0-9][0-9A-Z]?$/.test(outward) ? outward : null;
  }

  function zoneFor(cfg, postcode) {
    var outward = outwardCode(postcode);
    if (!outward) return null;
    for (var i = 0; i < cfg.zones.length; i++) {
      var codes = cfg.zones[i].outwards || [];
      if (codes.indexOf(outward) !== -1) return cfg.zones[i];
    }
    return null;
  }

  /* The windows on offer right now. MIRRORS windowsFor() in
   * app/lib/sameday.js, including the half-hour rounding and the minimum
   * final window — a customer must never be shown a different set of times
   * from the ones the admin preview promised. */
  function samedayWindows(cfg, nowMins) {
    return regularWindows(cfg, nowMins);
  }

  function regularWindows(cfg, nowMins) {
    if (!cfg.samedayOn) return { windows: [], reason: 'off' };
    if (cfg.samedayCutoff !== null && nowMins >= cfg.samedayCutoff) {
      return { windows: [], reason: 'past_cutoff' };
    }
    if (cfg.samedayEnd === null) return { windows: [], reason: 'misconfigured' };

    /* hoursAttr gives { from, to } per weekday, or null for a closed one —
     * NOT a { closed: true } flag. Reading it the other way round would have
     * offered same-day on a day the shop is shut. */
    var today = cfg.hours ? cfg.hours[cfg.today.getDay()] : null;
    if (!today) return { windows: [], reason: 'closed_today' };

    var start = nowMins + cfg.samedayLead;
    if (start < today.from + cfg.samedayLead) start = today.from + cfg.samedayLead;
    var last = Math.min(cfg.samedayEnd, today.to);

    if (start >= last) return { windows: [], reason: 'too_late_today' };
    start = Math.ceil(start / 30) * 30;
    if (start >= last) return { windows: [], reason: 'too_late_today' };

    var out = [];
    for (var from = start; from < last; from += cfg.samedayWindow) {
      var to = Math.min(from + cfg.samedayWindow, last);
      if (to - from < cfg.samedayMinWindow) break;
      out.push({ start: from, end: to, label: rangeLabel(from, to) });
    }
    if (!out.length) return { windows: [], reason: 'too_late_today' };

    if (cfg.samedayOpen) {
      out.push({ start: start, end: last, open: true, label: cfg.samedayOpenLabel });
    }
    return { windows: out, reason: null };
  }

  function roots() {
    return $all('[data-ibc-fulfil]');
  }

  /* ==================================================================== */
  /* WHAT IS OFFERABLE — the seam. See the header note.                   */
  /* ==================================================================== */

  /* SAME-DAY COLLECTION IS DECIDED HERE, not in Liquid.
   *
   * It depends on the time of day, and Liquid's `now` is the moment the
   * section was rendered — a cached section would hand a customer a cutoff
   * that passed an hour ago. So the app's `same_day_pickup_enabled` and
   * `same_day_pickup_cutoff_time` arrive as data and the clock is read here.
   *
   * Caveat, and it is the browser's clock: a customer in another timezone
   * before their own local cutoff may be offered same-day collection they
   * cannot make. Bounded, and the alternative is a cutoff that lies to
   * everybody rather than to travellers. */
  function pickupLeadNow(cfg) {
    if (!cfg.sameDay || cfg.cutoff === null) return cfg.pickupLead;
    var now = new Date();
    return now.getHours() * 60 + now.getMinutes() < cfg.cutoff ? 0 : cfg.pickupLead;
  }

  /* THE DELIVERY CUT-OFF, for the same reason and with the same caveat as
   * the collection one: it is a time-of-day question, so it is decided
   * against the browser's clock rather than baked in by a section that may
   * have been cached an hour ago. Past the cut-off, today stops counting
   * towards the lead time. */
  function deliveryLeadNow(cfg) {
    if (cfg.deliveryCutoff === null) return cfg.deliveryLead;
    var now = new Date();
    var mins = now.getHours() * 60 + now.getMinutes();
    return mins >= cfg.deliveryCutoff ? cfg.deliveryLead + 1 : cfg.deliveryLead;
  }

  /* THE EARLIEST DATE WORTH OFFERING, before the calendar's own shading has a
   * say. For delivery that is today plus the lead in plain days; dayState then
   * refuses any closed weekday or blocked date at or after it, so the first
   * cell the customer can actually click is the first DELIVERABLE day on or
   * after this one. Two rules, each applied once. */
  function minDate(cfg, kind) {
    return kind === 'pickup'
      ? addDays(cfg.today, pickupLeadNow(cfg))
      : addDays(cfg.today, deliveryLeadNow(cfg));
  }

  function maxDate(cfg, kind) {
    return addDays(cfg.today, kind === 'pickup' ? cfg.window : cfg.deliveryWindow);
  }

  /* Returns 'ok' | 'early' | 'late' | 'closed'. Four answers rather than a
   * boolean, so the grid can style "not yet" differently from "we are shut"
   * and a screen reader can say which. */
  function dayState(cfg, kind, date) {
    var iso = toISO(date);
    if (date < minDate(cfg, kind)) return 'early';
    if (date > maxDate(cfg, kind)) return 'late';
    /* Two blackout lists, deliberately separate: the shop can be open when
     * the couriers are not, and a bank holiday is exactly that case. */
    var closedDates = kind === 'pickup' ? cfg.closedDates : cfg.deliveryClosedDates;
    if (closedDates.indexOf(iso) !== -1) return 'closed';
    var closedDays = kind === 'pickup' ? cfg.pickupClosedDays : cfg.deliveryClosedDays;
    if (closedDays.indexOf(date.getDay()) !== -1) return 'closed';

    /* For collection, a day with no hours has no slots, so offering it would
     * hand the customer an empty time grid. The hours table is the authority
     * and the closed-days list is a convenience that agrees with it. */
    if (kind === 'pickup' && !cfg.hours[date.getDay()]) return 'closed';

    /* AND A DAY WHOSE SLOTS HAVE ALL GONE PAST is 'early', not 'ok'.
     *
     * Preparation time makes this a live case rather than an edge one: a
     * basket with a whole cake in it needs three hours, so at 7pm on a day
     * the shop shuts at 9pm there is nothing left to book even though the
     * day is open and within the horizon. Reporting 'ok' here would light
     * the cell, let it be clicked, and then show an empty time grid.
     *
     * 'early' rather than 'closed' because that is what it is — too soon,
     * not shut — and the grid styles and announces the two differently. */
    if (kind === 'pickup' && slotsFor(cfg, date).length === 0) return 'early';

    return 'ok';
  }

  /* Every collection slot on a given day, as {start, end, time, label, from}.
   *
   * THE DAY'S OWN HOURS, not one pair for the week. The app's weekly_hours
   * gives every weekday its own start and end, and a Saturday that shuts at
   * 6pm should not be offered a 8:30pm slot because Friday is open late.
   *
   * The last slot is the last one that ENDS by closing time, which is why
   * the loop tests `start + slot <= to` rather than `start <= to`. Offering
   * a 20:45 slot on a 21:00 close would be promising a collection fifteen
   * minutes after the door is locked. */
  function slotsFor(cfg, date) {
    var out = [];
    var day = cfg.hours[date.getDay()];
    if (!day || cfg.slot <= 0) return out;
    var iso = toISO(date);

    /* THE PREPARATION FLOOR. Nothing may be offered sooner than
     * now + the basket's preparation minutes, which is the largest
     * custom.pickup_delay_minutes among its products with a 60-minute
     * floor. Three whole cakes do not take nine hours — the longest single
     * delay wins, and that decision is made in Liquid.
     *
     * This is also what stops a slot that has simply already happened being
     * offered: at 8pm, this morning's 10:00 is behind `earliest` like
     * everything else. Before this, same-day collection with a cut-off of
     * 21:00 offered a 10am slot at 8pm.
     *
     * The browser's clock again, and the same bounded caveat as the two
     * cut-offs: a customer whose device clock is wrong, or who is in
     * another timezone, gets an offer they may not be able to keep. The
     * alternative is a floor computed at render time, which a cached
     * section makes wrong for everybody. */
    var earliest = new Date(Date.now() + Math.max(0, cfg.prepMinutes) * 60000);

    for (var start = day.from; start + cfg.slot <= day.to; start += cfg.slot) {
      var end = start + cfg.slot;

      /* Built from the day's own midnight and the slot's minute offset, so
       * a slot pushed past midnight by the preparation time is compared on
       * the right day rather than wrapping. */
      var startsAt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
      startsAt.setMinutes(start);
      if (startsAt.getTime() < earliest.getTime()) continue;

      var a = minutesToClock(start);
      var b = minutesToClock(end);
      /* One suffix when the slot stays inside a half of the day, which is
       * the shop's existing spelling ("8:00–8:30pm"); both when it straddles
       * noon or midnight, where one suffix would be a lie. */
      var time = a.suffix === b.suffix
        ? a.text + '–' + b.text + b.suffix
        : a.text + a.suffix + '–' + b.text + b.suffix;
      out.push({
        /* The start WITH its meridiem. Dropping it made the tiles narrower
         * but left a column reading "1:00", which a customer glancing at a
         * collection grid can read as one in the morning. Seven characters
         * is worth paying for that. */
        from: a.text + a.suffix,
        start: iso + 'T' + pad(a.h24) + ':' + pad(a.m) + ':00' + cfg.tz,
        end: iso + 'T' + pad(b.h24) + ':' + pad(b.m) + ':00' + cfg.tz,
        time: time,
        label: labelLong(date) + ', ' + time
      });
    }
    return out;
  }

  /* ==================================================================== */
  /* THE CACHE OF WHAT WAS LAST WRITTEN                                   */
  /* ==================================================================== */

  /* THE STAGE IS NOT CART DATA, and is deliberately not in `state`.
   *
   * Which step of the drawer's flow is on screen is a view, not a fact about
   * the order — it has no business being written to a cart attribute, and a
   * customer who reloads should land where their DATA puts them rather than
   * wherever they happened to have got to. So Liquid derives an opening
   * stage from the attributes, this variable tracks it while they walk, and
   * a section swap hands the derivation back to Liquid.
   *
   * 'all' is the cart page: every stage on screen at once, no walking. */
  var stage = 'closed';

  var STAGES = ['closed', 'options', 'calendar', 'slots', 'review', 'all'];

  var state = {
    method: '',
    option: '',
    deliveryDate: '',
    deliveryLabel: '',
    pickupDate: '',
    pickupStart: '',
    pickupEnd: '',
    pickupLabel: '',
    /* SAME-DAY. Declared here rather than created on first use so that
     * snapshot() and restore() carry them — a failed write has to roll the
     * postcode back with everything else, or the tile shows a zone the cart
     * never accepted. */
    samedayPostcode: '',
    samedayZone: '',
    samedayPrice: '',
    samedayLabel: '',
    /* The window as instants, for the courier booking. The label is for the
     * customer; these are for Gophr. */
    samedayStart: '',
    samedayEnd: '',
    /* THE TIER, WHICH IS THE THING BEING BOUGHT.
     *
     * A tier is a deadline with a price on it. The label is what the
     * customer reads ("By 1:00pm"); the id is what the order carries; the
     * deadline is what Gophr is told; the price is what Shopify charges and
     * what the booking guard measures the real quote against.
     *
     * WHAT THE COURIER QUOTED IS NOT HERE, on purpose. It is a number in
     * the page and therefore a number in anybody's developer tools, and
     * what the shop pays its courier is not the customer's business. The
     * app records the real quote against the order at the moment it books,
     * where it is useful and private. */
    samedayTier: '',
    samedayDeadline: '',
    samedayReadyAt: '',
    samedayPricePence: '',
    /* WHETHER THE CHARGE IS ACTUALLY IN THE BASKET. Not the same question as
     * whether a tier was clicked: the click is instant and the cart write is
     * a round trip that can fail. */
    samedayLineOk: false
  };

  /* THE LAST STATE THE CART IS KNOWN TO HAVE ACCEPTED.
   *
   * Not the same thing as `state`, which runs ahead of the network so the UI
   * can answer a click immediately. When a write fails, THIS is what the UI
   * is put back to.
   *
   * It cannot be recovered by re-reading the DOM: `syncRoot` stamps the
   * optimistic choice onto the root's own data attributes, so by the time a
   * rejection arrives the markup already agrees with the write that failed.
   * That is exactly the bug this variable exists to fix — the first version
   * rolled back by re-seeding from those attributes and therefore rolled
   * back to the thing it was trying to undo. */
  var committed = null;

  function snapshot() {
    var copy = {};
    Object.keys(state).forEach(function (key) { copy[key] = state[key]; });
    return copy;
  }

  function restore(saved) {
    if (!saved) return;
    Object.keys(saved).forEach(function (key) { state[key] = saved[key]; });
  }

  /* Seeded from the markup Liquid rendered, so a reload, a back button or a
   * section refresh all start from the cart rather than from zero. */
  function seed() {
    var root = roots()[0];
    if (!root) return;
    state.method = root.getAttribute('data-method') || '';
    state.option = root.getAttribute('data-option') || '';
    state.deliveryDate = root.getAttribute('data-delivery-date') || '';
    state.pickupDate = root.getAttribute('data-pickup-date') || '';
    state.pickupStart = root.getAttribute('data-pickup-start') || '';

    var d = parseISO(state.deliveryDate);
    state.deliveryLabel = d ? labelShort(d) : '';

    var p = parseISO(state.pickupDate);
    if (p && state.pickupStart) {
      var cfg = conf(root);
      var matches = slotsFor(cfg, p).filter(function (slot) {
        return slot.start === state.pickupStart;
      });
      if (matches.length) {
        state.pickupEnd = matches[0].end;
        state.pickupLabel = matches[0].label;
      }
    }

    /* Liquid just told us what the cart holds, so this IS the known-good
     * baseline — including after a section swap, where the server has had
     * the last word. */
    committed = snapshot();

    /* And it has also worked out which step that data belongs on. Trusting
     * its answer rather than keeping our own is what makes the flow survive
     * a quantity change mid-walk. */
    var fromLiquid = root.getAttribute('data-stage') || 'closed';
    stage = STAGES.indexOf(fromLiquid) === -1 ? 'closed' : fromLiquid;
  }

  function isFooter(root) {
    return root.getAttribute('data-layout') === 'footer';
  }

  /* Where "back" and "change" land, and where a fresh choice moves to. One
   * table rather than transitions scattered through the handlers, because
   * the flow is the thing most likely to be revised. */
  function nextStage(what) {
    if (stage === 'all') return 'all';
    if (what === 'method') return state.method === 'pickup' ? 'calendar' : 'options';
    if (what === 'option') {
      if (state.option === 'scheduled') return 'calendar';
      /* SAME-DAY STAYS ON THE OPTIONS STEP, and this is the one place the
       * three speeds genuinely differ.
       *
       * Standard is finished the moment it is chosen, and the calendar has a
       * step of its own to move to. Same-day has neither: its postcode box
       * and its window grid live INSIDE the options step, under the tile.
       * Advancing to review here hid the step they are in, so the customer
       * was shown a finished basket with no address checked and no window
       * picked — and the checkout button with it.
       *
       * It leaves for review when a WINDOW is chosen, not when the tile is. */
      if (state.option === 'sameday') return 'options';
      return 'review';
    }
    if (what === 'date') return state.method === 'pickup' ? 'slots' : 'review';
    if (what === 'slot') return 'review';
    if (what === 'window') return 'review';
    return stage;
  }

  /* WHERE THIS CART BELONGS. The same answer Liquid works out on first
   * paint, needed again whenever the panel is reopened — by the close
   * control, or by tapping the tile that is already lit. Reopening must land
   * on the step the DATA implies, not on the first step of the flow, or a
   * finished basket sends its customer back to the beginning. */
  function resumeStage() {
    if (ready()) return 'review';
    if (state.method === 'pickup') return state.pickupDate ? 'slots' : 'calendar';
    if (state.method === 'delivery') return state.option === 'scheduled' ? 'calendar' : 'options';
    return 'options';
  }

  function setStage(next) {
    if (stage === 'all') return;
    if (STAGES.indexOf(next) === -1) return;
    stage = next;
    syncAll();
  }

  function ready() {
    if (state.method === 'pickup') return !!(state.pickupStart && state.pickupLabel);
    if (state.method === 'delivery') {
      if (state.option === 'standard') return true;
      if (state.option === 'scheduled') return !!(state.deliveryDate && state.deliveryLabel);
      /* SAME-DAY NEEDS BOTH A ZONE AND A WINDOW. The zone is proof the
       * address can be reached; the window is the promise. The checkout
       * validation refuses the order without the label, so the basket must
       * not call itself ready without one. */
      /* SAME-DAY NEEDS THE CHARGE IN THE BASKET TOO.
       *
       * The zone proves we can reach them and the tier is the promise, but
       * neither of those takes any money: the courier charge rides as a line
       * item, and an order that reached checkout without it would be a
       * customer paying nothing for a £12 courier. So the line is part of
       * being ready, not a side effect of it. */
      if (state.option === 'sameday') {
        return !!(state.samedayZone && state.samedayLabel && state.samedayLineOk);
      }
    }
    return false;
  }

  function summaryText(root) {
    if (state.method === 'pickup') {
      return state.pickupLabel ? 'Collect: ' + state.pickupLabel : '';
    }
    if (state.method === 'delivery') {
      if (state.option === 'standard') {
        var standard = $one('[data-ibc-fulfil-choice="standard"] .ibc-fulfil__choice-title', root);
        return standard ? standard.textContent.trim() : 'Standard delivery';
      }
      if (state.option === 'scheduled' && state.deliveryLabel) {
        return 'Delivery: ' + state.deliveryLabel;
      }
      if (state.option === 'sameday' && state.samedayLabel) {
        return 'Today: ' + state.samedayLabel;
      }
    }
    return '';
  }

  /* ==================================================================== */
  /* WRITING TO THE CART                                                  */
  /* ==================================================================== */

  /* One chain, so two fast clicks cannot land out of order and leave the
   * cart holding the earlier choice. */
  var chain = Promise.resolve();
  var pending = 0;

  function attributesFor() {
    /* Every key is always sent. An empty string DELETES a cart attribute in
     * Shopify, which is what clears the other half — omitting the key would
     * leave a stale collection slot attached to a shipped order. */
    var out = {
      delivery_method: state.method || '',
      delivery_option: state.method === 'delivery' ? state.option || '' : '',
      /* SAME-DAY WRITES THE SAME TWO KEYS AS A DATED DELIVERY, and that is
       * deliberate. The checkout validation, the orders app and the kitchen
       * all want to know WHICH DAY and WHICH WINDOW, and a same-day order
       * answers both — today, and the window chosen. Inventing a third pair
       * of keys would have meant teaching three other things to read them. */
      delivery_date: datedNow() ? state.deliveryDate || '' : '',
      delivery_label: datedNow() ? state.deliveryLabel || '' : '',
      /* THE WINDOW AS INSTANTS. Written only for same-day, cleared for
       * everything else — a dated delivery has a day, not an hour, and a
       * standard one has neither.
       *
       * These are what the courier booking reads. Without them it refuses the
       * order and asks a human, which is the correct behaviour for an order
       * placed before this attribute existed, and the reason the app flags
       * rather than guesses. */
      delivery_window_start:
        state.option === 'sameday' ? state.samedayStart || '' : '',
      delivery_window_end:
        state.option === 'sameday' ? state.samedayEnd || '' : '',
      /* THE TIER AND ITS PRICE. New keys rather than new meanings for the
       * old ones: delivery_window_start and delivery_window_end keep saying
       * exactly what they have always said, so the orders app, the
       * validation function and every order already placed go on reading
       * what they expect. */
      /* THE POSTCODE THE PRICE WAS QUOTED FOR.
       *
       * Not for the courier — the rider goes to the order's real shipping
       * address, which the customer types at checkout and may well change
       * after this basket was priced. This is here so that change can be
       * CAUGHT: the delivery function compares it against the address and
       * refuses to offer same-day when the two disagree, and the validation
       * function says so in words. Without it, a quote for W1T could pay for
       * a journey to SW11. */
      delivery_postcode: state.option === 'sameday' ? state.samedayPostcode || '' : '',
      delivery_tier: state.option === 'sameday' ? state.samedayTier || '' : '',
      delivery_deadline: state.option === 'sameday' ? state.samedayDeadline || '' : '',
      delivery_ready_at: state.option === 'sameday' ? state.samedayReadyAt || '' : '',
      delivery_price_pence: state.option === 'sameday' ? String(state.samedayPricePence || '') : '',
      ibc_pickup_requested: state.method === 'pickup' ? 'true' : '',
      ibc_pickup_date: state.method === 'pickup' ? state.pickupDate || '' : '',
      ibc_pickup_slot_start: state.method === 'pickup' ? state.pickupStart || '' : '',
      ibc_pickup_slot_end: state.method === 'pickup' ? state.pickupEnd || '' : '',
      ibc_pickup_slot_label: state.method === 'pickup' ? state.pickupLabel || '' : '',
      ibc_pickup_location: state.method === 'pickup' ? pickupLocation() : '',
      /* THE PREPARATION TIME THE SLOT WAS CHOSEN UNDER.
       *
       * Not ours to drop. Every collection order the shop has ever taken
       * carries this — #1054 and #1064 through #1083 all have it, reading 60
       * or 180 — because the old scheduler wrote it, and the orders app reads
       * what the orders carry. The first version of this picker computed the
       * number and then failed to write it, which would have left new orders
       * looking different from old ones for no reason anybody could see.
       *
       * It is the basket's own figure: the largest custom.pickup_delay_minutes
       * among the products in it, floored at 60, which is what actually
       * constrained which slots were offered. Worth having on the order as a
       * record of the rule that applied at the time, since the product's
       * metafield can be edited afterwards. */
      ibc_pickup_delay_minutes: state.method === 'pickup' ? String(prepMinutes()) : ''
    };
    return out;
  }

  /* Which options carry a date and a label. One predicate rather than the
   * same condition written twice, because the day the two drift is the day
   * an order carries a label with no date. */
  function datedNow() {
    return state.method === 'delivery' &&
      (state.option === 'scheduled' || state.option === 'sameday');
  }

  function pickupLocation() {
    var root = roots()[0];
    return root ? root.getAttribute('data-location') || '' : '';
  }

  function prepMinutes() {
    var root = roots()[0];
    return root ? conf(root).prepMinutes : 60;
  }

  function showError(message) {
    var slot = $one('[data-ibc-cart-error]');
    if (slot) slot.textContent = message;
  }

  function clearError() {
    $all('[data-ibc-cart-error]').forEach(function (slot) {
      slot.textContent = '';
    });
  }

  /* ==================================================================== */
  /* THE DELIVERY CHARGE, AS A LINE IN THE BASKET                         */
  /*                                                                       */
  /* Shopify Basic cannot ask an app for a shipping price at checkout —    */
  /* that is the Carrier Service API and it starts at Advanced. So the     */
  /* courier charge arrives as an ordinary line item, priced by picking    */
  /* the variant whose price matches the tier. Local delivery itself is    */
  /* set to £0 and exists only to decide WHETHER we can reach an address,  */
  /* and to give checkout its mobile-number and delivery-instructions      */
  /* fields, which is where the rider's directions come from.              */
  /*                                                                       */
  /* NOTHING HERE IS HARD-CODED BUT THE HANDLE. The variant ids are read   */
  /* from the product's own JSON, so the ladder can be re-priced in the    */
  /* admin without touching the theme, and a theme deployed against a shop */
  /* that has not got the product simply refuses same-day rather than      */
  /* selling a delivery it cannot charge for.                              */

  var LADDER_HANDLE = 'ibc-same-day-delivery';
  var ladderPromise = null;

  /* The ladder, once per page. A map of pence to variant id, plus the
   * product id so an existing line can be recognised whatever rung it is on. */
  function ladderCatalogue() {
    if (ladderPromise) return ladderPromise;
    ladderPromise = fetch('/products/' + LADDER_HANDLE + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('ladder_missing');
        return response.json();
      })
      .then(function (product) {
        var byPence = {};
        (product.variants || []).forEach(function (variant) {
          /* Shopify gives variant prices in pence already. */
          byPence[String(variant.price)] = variant.id;
        });
        return { productId: product.id, byPence: byPence };
      })
      .catch(function (error) {
        /* NOT CACHED AS A FAILURE. A page loaded during a deploy would
         * otherwise refuse same-day until it was reloaded. */
        ladderPromise = null;
        throw error;
      });
    return ladderPromise;
  }

  /* Put the basket's delivery line in agreement with the chosen tier.
   *
   * `pence` null means there should be no line at all — the customer has
   * switched to collection, to standard delivery, or has not chosen a tier.
   *
   * One /cart/update.js call does the whole job: the old rung to zero and the
   * new rung to one, together. Removing and then adding would leave a window
   * where the basket had no delivery charge, and a customer who pressed
   * checkout inside it would pay nothing for their courier. */
  function syncLadderLine(pence) {
    var wanted = pence === null || pence === '' || pence === undefined
      ? null
      : String(parseInt(pence, 10));

    return ladderCatalogue()
      .then(function (ladder) {
        if (wanted !== null && !ladder.byPence[wanted]) {
          /* A price with no rung. Should be impossible — the server prices
           * off the same ladder — but selling a delivery we cannot charge
           * for is the one outcome worth refusing outright. */
          throw new Error('no_rung');
        }
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : { items: [] }; })
          .then(function (cart) { return { ladder: ladder, cart: cart }; });
      })
      .then(function (both) {
        var ladder = both.ladder;
        var updates = {};
        var already = false;
        var wantedId = wanted === null ? null : ladder.byPence[wanted];

        (both.cart.items || []).forEach(function (item) {
          if (item.product_id !== ladder.productId) return;
          if (wantedId && item.variant_id === wantedId && item.quantity === 1) {
            already = true;
            return;
          }
          /* Every other rung goes, including a duplicate of the right one at
           * the wrong quantity. */
          updates[String(item.variant_id)] = 0;
        });

        if (wantedId && !already) updates[String(wantedId)] = 1;

        if (!Object.keys(updates).length) return already || wanted === null;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) {
          if (!response.ok) throw new Error('cart_refused');
          return true;
        });
      });
  }

  function commit() {
    var attempt = snapshot();
    pending++;
    setPending(true);
    chain = chain
      .then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributesFor() })
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error(MSG.failed);
        return response.json();
      })
      .then(function () {
        /* AND THE MONEY, AFTER THE ATTRIBUTES.
         *
         * In that order deliberately. The attributes say what was promised
         * and the line says what is being charged for it; if the second
         * fails, an order cannot reach checkout at all, because the gate
         * requires samedayLineOk. The reverse order would leave a basket
         * carrying a charge for a promise it had not recorded. */
        var wanted = attempt.option === 'sameday' && attempt.method === 'delivery'
          ? attempt.samedayPricePence || null
          : null;
        return syncLadderLine(wanted).then(function () {
          state.samedayLineOk = wanted !== null;
          /* AND ONTO THE SNAPSHOT.
           *
           * `attempt` was taken before the charge existed, so it says false.
           * Storing it as `committed` without this would mean the next failed
           * write restored a state in which the basket was not chargeable —
           * and the checkout button would go dead again for no visible
           * reason. */
          attempt.samedayLineOk = state.samedayLineOk;
        });
      })
      .then(function () {
        /* The cart took it. Everything up to and including this write is now
         * the thing to fall back to. */
        committed = attempt;
        clearError();
        /* AND REDRAW.
         *
         * samedayLineOk is what the checkout button waits for, and it becomes
         * true here — after the last syncAll() has already run. Without this
         * the basket sat with a chosen time, a charge in the cart and a dead
         * CHECKOUT button, which is exactly how it looked. */
        syncAll();
        /* Tell the rest of the theme its totals are stale. The delivery line
         * has just changed the cart and the drawer renders its own subtotal;
         * whether anything is listening is the theme's business, but not
         * saying so guarantees a wrong number on screen. */
        try {
          document.dispatchEvent(new CustomEvent('ibc:cart-changed', { bubbles: true }));
        } catch (e) { /* older browsers: the totals just stay stale */ }
      })
      .catch(function (error) {
        /* A BASKET THAT CANNOT BE CHARGED IS NOT A BASKET THAT CAN CHECK OUT.
         * Cleared before restore() so a stale true cannot survive a failure
         * somewhere else in the chain. */
        state.samedayLineOk = false;
        if (error && (error.message === 'ladder_missing' || error.message === 'no_rung')) {
          restore(committed);
          syncAll();
          showError('Same-day delivery is not available right now. Please choose a delivery date instead.');
          /* Resolved, not rethrown: the generic handler below would show the
           * wrong message. `pending` is NOT touched here — the tail of the
           * chain owns it, and decrementing twice would leave the basket
           * stuck looking busy for the rest of the session. */
          return;
        }
        return Promise.reject(error);
      })
      .catch(function () {
        /* Put the UI back to the last state the cart actually accepted,
         * rather than leaving a selection on screen that no order will ever
         * carry. */
        restore(committed);
        syncAll();
        showError(MSG.failed);
      })
      .then(function () {
        pending--;
        if (pending <= 0) setPending(false);
      });
    return chain;
  }

  function setPending(on) {
    roots().forEach(function (root) {
      root.classList.toggle('is-saving', !!on);
    });
  }

  /* ==================================================================== */
  /* RENDERING                                                            */
  /* ==================================================================== */

  function monthShown(root, kind) {
    var grid = $one('[data-ibc-cal="' + kind + '"] [data-ibc-cal-grid]', root);
    if (!grid) return null;
    var raw = grid.getAttribute('data-month');
    return raw ? parseISO(raw) : null;
  }

  /* The month a calendar should open on: the chosen date if there is one,
   * otherwise the first date that can actually be chosen — never "today",
   * which on the 30th would open a month with nothing selectable in it. */
  function openMonth(cfg, kind) {
    var chosen = parseISO(kind === 'pickup' ? state.pickupDate : state.deliveryDate);
    var from = chosen || minDate(cfg, kind);
    return new Date(from.getFullYear(), from.getMonth(), 1, 12, 0, 0, 0);
  }

  function renderCalendar(root, kind, monthStart) {
    var wrap = $one('[data-ibc-cal="' + kind + '"]', root);
    if (!wrap) return;
    var grid = $one('[data-ibc-cal-grid]', wrap);
    var title = $one('[data-ibc-cal-month]', wrap);
    if (!grid) return;

    var cfg = conf(root);
    var first = monthStart || openMonth(cfg, kind);
    grid.setAttribute('data-month', toISO(first));
    if (title) title.textContent = MONTHS[first.getMonth()] + ' ' + first.getFullYear();

    var chosenISO = kind === 'pickup' ? state.pickupDate : state.deliveryDate;

    /* Monday-first: JS gives 0 for Sunday, so Sunday is 6 columns in. */
    var lead = (first.getDay() + 6) % 7;
    var daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

    /* A day cell that currently holds focus is about to be destroyed by the
     * innerHTML below. Remember which one, and hand focus back to the same
     * date afterwards — otherwise selecting a day with Enter drops the
     * keyboard user back at the top of the document. */
    var active = document.activeElement;
    var refocusISO =
      active && grid.contains(active) && active.hasAttribute('data-ibc-cal-day')
        ? active.getAttribute('data-ibc-cal-day')
        : null;

    var html = '';
    var total = Math.ceil((lead + daysInMonth) / 7) * 7;
    var focusISO = null;

    for (var i = 0; i < total; i++) {
      if (i % 7 === 0) html += '<div class="ibc-cal__row" role="row">';

      var dayNum = i - lead + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        html += '<div class="ibc-cal__cell ibc-cal__cell--blank" role="gridcell"></div>';
      } else {
        var date = new Date(first.getFullYear(), first.getMonth(), dayNum, 12, 0, 0, 0);
        var iso = toISO(date);
        var verdict = dayState(cfg, kind, date);
        var isChosen = iso === chosenISO;
        var disabled = verdict !== 'ok';
        if (!disabled && !focusISO) focusISO = iso;
        if (isChosen) focusISO = iso;

        html +=
          '<div class="ibc-cal__cell" role="gridcell">' +
          '<button type="button" class="ibc-cal__day' +
          (isChosen ? ' is-chosen' : '') +
          (disabled ? ' is-off' : '') +
          '" data-ibc-cal-day="' + iso + '"' +
          ' data-verdict="' + verdict + '"' +
          (disabled ? ' aria-disabled="true"' : '') +
          ' aria-pressed="' + (isChosen ? 'true' : 'false') + '"' +
          ' tabindex="-1"' +
          '><span class="visually-hidden">' + labelCell(date) +
          (verdict === 'closed' ? ' — closed' : '') +
          (verdict === 'early' ? ' — too soon' : '') +
          (verdict === 'late' ? ' — too far ahead' : '') +
          '</span><span aria-hidden="true">' + dayNum + '</span></button>' +
          '</div>';
      }

      if (i % 7 === 6) html += '</div>';
    }

    grid.innerHTML = html;

    /* Roving tabindex: exactly one day in the grid is reachable by Tab. */
    var target = focusISO ? $one('[data-ibc-cal-day="' + focusISO + '"]', grid) : null;
    if (!target) target = $one('.ibc-cal__day:not([aria-disabled="true"])', grid);
    if (target) target.setAttribute('tabindex', '0');

    if (refocusISO) {
      var back = $one('[data-ibc-cal-day="' + refocusISO + '"]', grid);
      if (back) {
        if (target && target !== back) target.setAttribute('tabindex', '-1');
        back.setAttribute('tabindex', '0');
        back.focus();
      }
    }

    /* Nav buttons: nothing before the first offerable month, nothing after
     * the window closes. A calendar that pages into an empty year is a
     * calendar the customer has to be told to stop using. */
    var prevEnd = new Date(first.getFullYear(), first.getMonth(), 0, 12, 0, 0, 0);
    var nextStart = new Date(first.getFullYear(), first.getMonth() + 1, 1, 12, 0, 0, 0);
    var prev = $one('[data-ibc-cal-nav="-1"]', wrap);
    var next = $one('[data-ibc-cal-nav="1"]', wrap);
    if (prev) prev.disabled = prevEnd < minDate(cfg, kind);
    if (next) next.disabled = nextStart > maxDate(cfg);
  }

  function renderSlots(root) {
    var wrap = $one('[data-ibc-fulfil-slots]', root);
    if (!wrap) return;
    var grid = $one('[data-ibc-fulfil-slot-grid]', wrap);
    var empty = $one('[data-ibc-fulfil-slots-empty]', wrap);
    if (!grid) return;

    var date = parseISO(state.pickupDate);
    wrap.hidden = !date;
    if (!date) {
      grid.innerHTML = '';
      return;
    }

    var cfg = conf(root);
    var slots = slotsFor(cfg, date);
    if (empty) empty.hidden = slots.length > 0;

    grid.innerHTML = slots
      .map(function (slot) {
        var chosen = slot.start === state.pickupStart;
        return (
          '<button type="button" class="ibc-fulfil__slot' + (chosen ? ' is-chosen' : '') + '"' +
          ' data-ibc-fulfil-slot="' + slot.start + '"' +
          ' data-end="' + slot.end + '"' +
          ' data-label="' + slot.label.replace(/"/g, '&quot;') + '"' +
          ' role="radio" aria-checked="' + (chosen ? 'true' : 'false') + '"' +
          ' tabindex="' + (chosen ? '0' : '-1') + '">' +
          /* TWO SPELLINGS OF THE SAME TIME, one shown per layout by CSS.
           *
           * The full range is the accessible name and what the cart page
           * shows. The drawer footer shows the START only, because five
           * columns of "10:00" fit where three columns of "10:00–10:30am"
           * do not, and on a 667px phone that difference is what decides
           * whether the checkout button is on screen. The window length is
           * stated once above the grid instead of seventeen times inside
           * it. */
          '<span class="ibc-fulfil__slot-full">' + slot.time + '</span>' +
          '<span class="ibc-fulfil__slot-short" aria-hidden="true">' + slot.from + '</span>' +
          '</button>'
        );
      })
      .join('');

    if (!$one('.ibc-fulfil__slot[tabindex="0"]', grid)) {
      var firstSlot = $one('.ibc-fulfil__slot', grid);
      if (firstSlot) firstSlot.setAttribute('tabindex', '0');
    }
  }

  /* WHAT HAS BEEN CHOSEN, each line editable.
   *
   * ONE list, rendered once, sitting outside the expanding body so it stays
   * on screen when the panel is put away. It grows as the flow is walked:
   * the method as soon as a tile is tapped, then the speed or the date, then
   * the time.
   *
   * `stage` decides what is SETTLED, not `state`. On the times step a date
   * is settled and a time is not, even though both may be in the cache from
   * an earlier visit — showing a time the customer is in the middle of
   * re-choosing would be describing the past as the present.
   */
  /* THE SETTLED BAR. One line that stands in for the whole picker once the
   * choice is complete and the panel is put away.
   *
   * Shown only when BOTH are true: the basket is ready, and the stage is
   * 'closed'. Ready without closed is somebody mid-flow who has just finished
   * — the review is the right thing to look at, not a summary of it. Closed
   * without ready is a half-made choice, and summarising that would be
   * claiming something was decided when it was not.
   *
   * Returns nothing; it writes into the DOM and lets syncRoot decide what is
   * hidden around it. */
  function renderBar(root) {
    var bar = $one('[data-ibc-fulfil-bar]', root);
    if (!bar) return;

    var slot = $one('[data-ibc-fulfil-bar-text]', bar);
    var on = ready() && stage === 'closed';
    bar.hidden = !on;
    if (!on || !slot) return;

    /* SHORT FORMS THROUGHOUT. This is one line on a 375px phone beside an
     * icon and the word Change, so "Sat 20th Sept" rather than "Saturday 20
     * September". The long form is still one tap away. */
    var method = '';
    var detail = '';

    if (state.method === 'pickup') {
      method = 'Collect';
      var pd = parseISO(state.pickupDate);
      detail = (pd ? labelShort(pd) : '');
      var slotText = state.pickupLabel ? slotTimeOf(state.pickupLabel) : '';
      if (slotText) detail = detail ? detail + ', ' + slotText : slotText;
    } else if (state.method === 'delivery') {
      method = 'Shipping';
      detail = state.option === 'scheduled'
        ? (state.deliveryLabel || '')
        : (root.getAttribute('data-standard-label') || '');
    }

    slot.innerHTML =
      '<span class="ibc-fulfil__bar-method">' + esc(method) + '</span>' +
      (detail ? '<span class="ibc-fulfil__bar-detail">' + esc(detail) + '</span>' : '');

    /* The accessible name is the whole sentence, because the bar is one
     * button and "Change" on its own tells a screen-reader user nothing about
     * what they would be changing. */
    bar.setAttribute(
      'aria-label',
      method + (detail ? ', ' + detail : '') + '. Change.'
    );
  }

  function renderChosen(root) {
    var host = $one('[data-ibc-fulfil-chosen="live"]', root);
    if (!host) return;

    var rows = [];

    /* SHOW EVERYTHING SETTLED, AND HIDE ONLY WHAT IS BEING RE-CHOSEN.
     *
     * The earlier rule was the other way round — a detail appeared only once
     * the flow had reached the review — and it had a bug worth keeping a
     * note about: collapsing the panel moved the stage to 'closed', so the
     * customer's own date vanished from the one thing that was supposed to
     * survive collapsing.
     *
     * A step in progress is the only reason to hold a value back: showing
     * "Date: Sunday 13th" while somebody is mid-way through picking a
     * different day describes the past as the present. Everything else,
     * including when nothing is in progress at all, is shown. */
    function settled(owningStage) {
      return stage !== owningStage;
    }

    /* NO METHOD ROW. "Shipping — By courier — Change" and "Collect — Italian
     * Bear Chocolate — Change" both restated what the lit tile directly above
     * already says, and offered a Change control for a choice the tiles
     * themselves are. Two rows of a footer, spent on nothing.
     *
     * What is left is only what the tiles CANNOT show: the speed, the date,
     * the time. So this list is empty until one of those is settled, which is
     * also why it collapses to nothing on the two steps where the customer
     * has picked a method and no detail yet. */
    if (state.method === 'delivery') {
      if (state.option === 'standard' && settled('options')) {
        rows.push({
          label: 'Speed',
          value: root.getAttribute('data-standard-label') || 'Standard',
          edit: 'options'
        });
      } else if (state.option === 'scheduled' && state.deliveryLabel && settled('calendar')) {
        rows.push({ label: 'Arrives', value: state.deliveryLabel, edit: 'calendar' });
      } else if (state.option === 'sameday' && state.samedayLabel && settled('options')) {
        /* TWO ROWS, LIKE COLLECTION, not one. A courier booking is a day and
         * a window, and the two read as one sentence only while the window
         * is a clock range — "Today, any time before 9pm" is not something
         * anyone writes. Splitting them also gives the review a Change
         * control, which it had none of at all: a customer who booked the
         * wrong window reached a review with nothing in it and no way back
         * to the grid. Both rows edit the OPTIONS step, because that is
         * where the postcode box and the window grid live. */
        rows.push({ label: 'Date', value: 'Today', edit: 'options' });
        rows.push({ label: 'Time', value: state.samedayLabel, edit: 'options' });
      }
    } else if (state.method === 'pickup') {
      var d = parseISO(state.pickupDate);
      if (d && settled('calendar')) {
        rows.push({ label: 'Date', value: labelLong(d), edit: 'calendar' });
      }
      if (state.pickupLabel && settled('slots')) {
        rows.push({ label: 'Time', value: slotTimeOf(state.pickupLabel), edit: 'slots' });
      }
    }

    host.innerHTML = rows
      .filter(function (row) { return row.value; })
      .map(function (row) {
        return (
          '<div class="ibc-fulfil__row">' +
          '<span class="ibc-fulfil__row-label">' + esc(row.label) + '</span>' +
          '<span class="ibc-fulfil__row-value">' + esc(row.value) + '</span>' +
          '<button type="button" class="ibc-fulfil__row-edit" data-ibc-fulfil-edit="' + row.edit + '">' +
          'Change<span class="visually-hidden"> ' + esc(row.label.toLowerCase()) + '</span>' +
          '</button></div>'
        );
      })
      .join('');
  }

  function esc(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* 'Sunday 20 September, 6:00-6:30pm' -> '6:00-6:30pm'. The date is already
   * its own row, so repeating it in the time row would be noise. */
  function slotTimeOf(label) {
    var at = String(label || '').lastIndexOf(', ');
    return at === -1 ? String(label || '') : label.slice(at + 2);
  }

  function syncRoot(root) {
    var footer = isFooter(root);

    /* THE SUMMARY STATE. One class, and the stylesheet does the rest: the
     * heading, the tiles and the detail rows all go, and the bar takes their
     * place. Kept as a class rather than six `hidden` attributes so that the
     * whole swap is one rule to read and one rule to undo. */
    if (footer) {
      renderBar(root);
      root.classList.toggle('is-summary', ready() && stage === 'closed');
    }

    /* TABS, and the case where NEITHER is lit.
     *
     * The drawer starts with no method chosen, so `open` can legitimately be
     * the empty string and both panels stay hidden. The roving tabindex then
     * has nobody to rove to, so the FIRST tab holds it — a tab strip that
     * cannot be reached by Tab at all is worse than one whose entry point is
     * arbitrary. */
    var fallback = root.getAttribute('data-preselect') === '0'
      ? ''
      : (root.getAttribute('data-open-tab') || 'delivery');
    var open = state.method || fallback;

    $all('[data-ibc-fulfil-tab]', root).forEach(function (tab, index) {
      var mine = tab.getAttribute('data-ibc-fulfil-tab');
      var on = mine === open;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on || (!open && index === 0) ? '0' : '-1');
      var panel = $one('[data-ibc-fulfil-panel="' + mine + '"]', root);
      if (panel) panel.hidden = !on;
    });

    /* Delivery options */
    $all('[data-ibc-fulfil-option]', root).forEach(function (input) {
      var on = state.method === 'delivery' && input.value === state.option;
      input.checked = on;
      /* The card around the radio is highlighted with `:has()` in CSS, which
       * is not everywhere yet. This class is the same rule, stated twice, so
       * the selection is legible in a browser that does not support it. */
      var card = closest(input, '[data-ibc-fulfil-choice]');
      if (card) card.classList.toggle('is-chosen', on);
    });
    /* Calendars — only the visible one is worth drawing, but the hidden one
     * is drawn too so switching tabs never shows an empty month for a frame. */
    renderCalendar(root, 'delivery', monthShown(root, 'delivery'));
    renderCalendar(root, 'pickup', monthShown(root, 'pickup'));
    renderSlots(root);

    /* SAME-DAY IS SYNCED FOR BOTH LAYOUTS, and the first version was not:
     * it sat inside the footer-only branch below, so the cart page rendered
     * the tile, let it be chosen, and never revealed the postcode box it
     * needs. The footer branch owns which STAGE is on screen; what is inside
     * a stage is everybody's business.
     *
     * (It was also nested inside the caution block for a while, so it only
     * ran on themes that happen to render a caution paragraph. Two ways for
     * one block to be in the wrong place is a fair warning about how deep
     * this function has got.) */
    var sameday = $one('[data-ibc-fulfil-sameday]', root);
    if (sameday) {
      sameday.hidden = !(state.method === 'delivery' && state.option === 'sameday');
    }

    /* PUT THE GRID BACK IF SOMETHING TOOK IT AWAY.
     *
     * Cheap and idempotent: it only fires when we hold an answer for the
     * postcode the basket still has AND the grid is empty, which is only
     * true after the region has been re-rendered from the server. Without
     * it, a customer who chose a tier watched every option disappear. */
    if (lastQuote && lastQuote.postcode === state.samedayPostcode &&
        state.option === 'sameday') {
      var grid = $one('[data-ibc-fulfil-window-grid]', root);
      if (grid && !grid.children.length) {
        renderTiers(root, lastQuote.data);
        var wrap = $one('[data-ibc-fulfil-windows]', root);
        if (wrap) wrap.hidden = false;
      }
    }

    /* THE CHOSEN WINDOW, MARKED. renderWindows() builds the grid once, at
     * the moment a postcode is checked, and nothing has been chosen yet at
     * that moment — so without this the customer tapped a window and the
     * grid showed no sign of it. Re-applied on every sync rather than in
     * chooseWindow(), so a grid rebuilt for a second postcode comes back
     * marked too. */
    $all('[data-ibc-fulfil-window]', root).forEach(function (button) {
      /* BY ID, NOT BY LABEL. Two tiers can share a label late in the day —
       * and a label is display text that the shop may reword, whereas an id
       * is a contract. */
      var mine = state.option === 'sameday' &&
        button.getAttribute('data-tier-id') === state.samedayTier &&
        !!state.samedayTier;
      button.classList.toggle('is-chosen', mine);
      button.setAttribute('aria-checked', mine ? 'true' : 'false');
    });

    /* STAGES. Exactly one block on screen in the footer layout; all of them
     * on the cart page, where `stage` is the sentinel 'all'. */
    if (footer) {
      $all('[data-ibc-fulfil-stage]', root).forEach(function (block) {
        block.hidden = block.getAttribute('data-ibc-fulfil-stage') !== stage;
      });

      renderChosen(root);

      /* The dated-delivery caution belongs to exactly one situation. A
       * standard delivery has no date to hedge about, and a collection slot
       * is a commitment the shop has made rather than a courier's guess — so
       * showing it there would be untrue, not merely noisy. */
      var caution = $one('[data-ibc-fulfil-caution]', root);
      if (caution) {
        caution.hidden = !(state.method === 'delivery' && state.option === 'scheduled');
      }


      var body = $one('[data-ibc-fulfil-body]', root);
      var expanded = stage !== 'closed';
      root.classList.toggle('is-open', expanded);
      if (body) body.setAttribute('aria-hidden', expanded ? 'false' : 'true');

      var collapse = $one('[data-ibc-fulfil-collapse]', root);
      if (collapse) {
        collapse.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var says = $one('.visually-hidden', collapse);
        if (says) {
          says.textContent = expanded
            ? 'Hide the delivery options'
            : 'Show the delivery options';
        }
      }
    } else {
      /* The cart page shows every STEP at once, but not every branch of a
       * choice already made: the delivery calendar is still revealed by
       * "choose a date" rather than sitting open under a standard-delivery
       * basket. Mirrors f_show_delivery_cal in the snippet. */
      $all('[data-ibc-fulfil-stage]', root).forEach(function (block) {
        block.hidden = false;
      });
      var deliveryCal = $one(
        '[data-ibc-fulfil-panel="delivery"] [data-ibc-fulfil-stage="calendar"]', root);
      if (deliveryCal) {
        deliveryCal.hidden = !(state.method === 'delivery' && state.option === 'scheduled');
      }
      root.classList.add('is-open');
    }

    /* Summary */
    var summary = $one('[data-ibc-fulfil-summary]', root);
    var summaryTextNode = $one('[data-ibc-fulfil-summary-text]', root);
    var text = summaryText(root);
    if (summaryTextNode) summaryTextNode.textContent = ready() ? text : '';
    if (summary) {
      summary.classList.toggle('is-ready', ready());
      /* In the footer it is the one thing that survives collapsing, so it is
       * shown whenever there is something to say AND the flow is not already
       * showing the review. Two statements of the same choice, one above the
       * other, is clutter. */
      /* Footer: never hidden, because it is the live region and the chosen
       * rows carry the visible answer. Panel: shown only when there is one. */
      summary.hidden = footer ? false : !ready();
    }

    root.setAttribute('data-method', state.method);
    root.setAttribute('data-option', state.option);
    root.setAttribute('data-stage', stage);
    root.setAttribute('data-ready', ready() ? '1' : '0');
  }

  /* The checkout button, the close control and the footer's own layout are
   * NOT inside the picker — they are in each section's markup — so they are
   * driven separately and globally.
   *
   * VISIBILITY vs ENABLEMENT, which are different questions here:
   *
   *   hidden    only while the drawer's picker is shut AND nothing has been
   *             chosen. A checkout button under two untouched tiles is an
   *             invitation to press something that cannot work.
   *   disabled  whenever the basket is not ready. Opening the picker reveals
   *             the button immediately, greyed, so the customer can see what
   *             the steps are FOR before they have finished them.
   *
   * A basket that IS ready keeps its button whatever the panel is doing —
   * collapsing must never strand somebody one tap from paying. */
  function syncGate() {
    var open = ready();
    var anyFooter = roots().some(isFooter);
    var expanded = !anyFooter || stage !== 'closed';
    var chosen = !!state.method;

    $all('[data-ibc-fulfil-checkout]').forEach(function (button) {
      button.disabled = !open;
    });

    $all('[data-ibc-fulfil-checkout-wrap]').forEach(function (wrap) {
      wrap.hidden = anyFooter && !expanded && !open;
    });

    /* The close control only exists while there is something to close. */
    $all('[data-ibc-fulfil-collapse]').forEach(function (button) {
      button.hidden = !expanded;
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    /* The footer reserves the top-right corner for that control only when it
     * is there, so the subtotal is not permanently indented around nothing. */
    $all('[data-ibc-fulfil-foot]').forEach(function (foot) {
      foot.classList.toggle('is-fulfil-open', expanded);
      foot.classList.toggle('is-fulfil-chosen', chosen);
    });

    $all('[data-ibc-fulfil-gate]').forEach(function (note) {
      note.hidden = open;
      if (!open && !note.textContent.trim()) note.textContent = MSG.gate;
    });
  }

  function syncAll() {
    /* ONE BROKEN PICKER MUST NOT TAKE THE OTHER DOWN WITH IT.
     *
     * On /cart there are two roots on screen — the page and the drawer —
     * and they are redrawn in a loop. An exception in the first would have
     * skipped the second AND skipped the checkout gate below, which is the
     * thing that decides whether the button is live. A half-drawn picker is
     * survivable; a basket whose checkout button never wakes up is not. */
    roots().forEach(function (root) {
      try {
        syncRoot(root);
      } catch (error) {
        try { console.error('[ibc-fulfil] redraw failed', error); } catch (e) {}
      }
    });
    try {
      syncGate();
    } catch (error) {
      try { console.error('[ibc-fulfil] checkout gate failed', error); } catch (e) {}
    }
  }

  /* ==================================================================== */
  /* CHOICES                                                              */
  /* ==================================================================== */

  function chooseMethod(method) {
    /* Re-picking the method you are already on re-opens its step rather than
     * doing nothing. In the drawer that is the only way back out of a
     * collapsed picker by way of the tabs, and doing nothing there reads as
     * a dead button. */
    if (state.method === method) {
      stage = resumeStage();
      syncAll();
      return;
    }

    state.method = method;

    /* Switching method does NOT throw away the other half's choice in the
     * cache, so a customer who flips to collection and back finds their
     * delivery date still there. It IS cleared in the cart — see
     * attributesFor() — because an order must carry one answer, not two.
     *
     * Shipping no longer DEFAULTS to standard, though. In the staged flow the
     * point of the options step is to ask, and answering on the customer's
     * behalf the instant they tap "Shipping" would skip the step and land
     * them on a review of a decision they had not made. */
    stage = nextStage('method');
    syncAll();
    commit();
  }

  function chooseOption(option) {
    state.method = 'delivery';
    state.option = option;
    /* LEAVING SAME-DAY CLEARS ITS ANSWERS. A postcode and a window belong to
     * the choice that asked for them; carrying them into a dated delivery
     * would leave the cart holding a window nobody picked for a day nobody
     * chose. The same reasoning as a new date clearing a collection slot. */
    if (option !== 'sameday') clearSameday();
    stage = nextStage('option');
    syncAll();
    commit();
  }

  function clearSameday() {
    showTimes(false);
    state.samedayZone = '';
    state.samedayPrice = '';
    state.samedayLabel = '';
    state.samedayPostcode = '';
    state.samedayStart = '';
    state.samedayEnd = '';
    state.samedayTier = '';
    state.samedayDeadline = '';
    state.samedayReadyAt = '';
    state.samedayPricePence = '';
    if (state.option !== 'scheduled') {
      state.deliveryDate = '';
      state.deliveryLabel = '';
    }

    /* AND THE SCREEN WITH IT. Clearing only the state left the answer and
     * the grid standing: a customer who switched to standard and came back
     * found a price for a postcode the cart no longer held, and a row of
     * windows they could book without re-checking the address. The grid is
     * emptied rather than merely hidden so nothing stale can be clicked
     * through a CSS mistake. */
    roots().forEach(function (root) {
      var answer = $one('[data-ibc-fulfil-postcode-answer]', root);
      if (answer) {
        answer.hidden = true;
        answer.className = 'ibc-fulfil__pc-answer';
        answer.textContent = '';
      }
      var wrap = $one('[data-ibc-fulfil-windows]', root);
      if (wrap) wrap.hidden = true;
      var grid = $one('[data-ibc-fulfil-window-grid]', root);
      if (grid) grid.innerHTML = '';
      var input = $one('[data-ibc-fulfil-postcode]', root);
      if (input) input.value = '';
    });
  }

  /* THE POSTCODE ANSWER. Three outcomes and one live region, so a change of
   * mind announces once rather than twice.
   *
   * This is the BASKET'S copy of the zone list, not Shopify's. Shopify
   * decides the real thing at checkout from the real address; this only
   * lets the customer find out sooner. A disagreement between the two shows
   * up as an option offered here and absent there, which is why the
   * delivery-gate function falls back to ordinary delivery rather than
   * leaving an empty list. */
  function checkPostcode(root) {
    var input = $one('[data-ibc-fulfil-postcode]', root);
    if (!input) return;
    var cfg = conf(root);
    var raw = String(input.value || '').trim();
    var answer = $one('[data-ibc-fulfil-postcode-answer]', root);
    var wrap = $one('[data-ibc-fulfil-windows]', root);

    state.samedayPostcode = raw;

    /* EMPTY BOX. Not an error — the customer is mid-thought. */
    if (!raw) {
      clearOffer(root, answer, wrap);
      syncAll();
      return;
    }

    /* THE LOCAL ZONE CHECK STAYS, AND RUNS FIRST.
     *
     * It is the basket's own copy of the zone list, and it is wrong about
     * nothing except the future. Its job here is to refuse an obviously
     * out-of-area postcode WITHOUT a network call: no request to our app, no
     * four quotes to Gophr, no waiting, for an address no courier was ever
     * going to serve. The server checks again anyway — this is politeness,
     * not security. */
    var zone = zoneFor(cfg, raw);
    if (!zone) {
      clearOffer(root, answer, wrap);
      if (answer) {
        answer.hidden = false;
        answer.className = 'ibc-fulfil__pc-answer is-no';
        answer.textContent = cfg.samedayOut;
      }
      syncAll();
      commit();
      return;
    }

    state.samedayZone = zone.id || '';

    /* ---- and now the real prices ---------------------------------- */
    setQuoting(root, answer, true);

    quoteRequest(cfg, raw)
      .then(function (data) {
        /* THE CUSTOMER MOVED ON. Between asking and answering they may have
         * typed a different postcode, or left same-day entirely. Painting a
         * stale answer over a newer one is how somebody ends up buying a
         * delivery to an address they corrected. */
        if (state.samedayPostcode !== raw) return;

        setQuoting(root, answer, false);

        if (!data || data.ok !== true) {
          clearOffer(root, answer, wrap);
          if (answer) {
            answer.hidden = false;
            answer.className = 'ibc-fulfil__pc-answer is-no';
            answer.textContent = quoteMessage(data, cfg);
          }
          syncAll();
          commit();
          return;
        }

        state.samedayZone = (data.zone && data.zone.id) || state.samedayZone;
        state.samedayReadyAt = data.ready_at || '';

        /* THE CHOSEN TIER MAY NO LONGER EXIST. Prices are quoted per
         * deadline and deadlines expire: a customer who picked "By 1:00pm"
         * at 11:48 and came back at 12:10 must not still be holding it. */
        if (state.samedayTier && !tierById(data.tiers, state.samedayTier)) {
          state.samedayTier = '';
          state.samedayLabel = '';
          state.samedayDeadline = '';
          state.samedayPricePence = '';
        }

        renderTiers(root, data);
        /* FOLD THE OTHER OPTIONS AWAY.
         *
         * Once there are real prices on screen, next-day and pick-a-date are
         * noise between the customer and the thing they are choosing — and
         * on a phone they are the difference between reading the tiles and
         * scrolling for them. The button under the grid is the way back. */
        showTimes(true);
        if (answer) {
          answer.hidden = false;
          answer.className = 'ibc-fulfil__pc-answer is-yes';
          answer.textContent = offerLine(raw, data);
        }
        if (wrap) wrap.hidden = false;
        syncAll();
        /* NO commit() HERE, AND THAT IS THE WHOLE POINT.
         *
         * Rule 2 at the top of this file: assets/ibc-cart.js replaces the
         * entire contents of [data-ibc-cart-refresh] after every basket
         * mutation. Writing attributes is a basket mutation. So committing
         * here tore out the grid that had just been built and the prices
         * vanished a heartbeat after appearing — which is exactly what it
         * looked like.
         *
         * Nothing needs saving yet in any case. Checking a postcode is a
         * question, not a choice; the postcode is written to the cart when a
         * tier is actually picked, alongside the tier itself. The failure
         * branches above DO commit, because clearing a stale same-day
         * selection is a real change that must survive a reload. */
      })
      .catch(function () {
        if (state.samedayPostcode !== raw) return;
        setQuoting(root, answer, false);
        clearOffer(root, answer, wrap);
        if (answer) {
          answer.hidden = false;
          answer.className = 'ibc-fulfil__pc-answer is-no';
          answer.textContent = 'We could not check same-day prices just now. ' +
            'Try again, or choose a delivery date instead.';
        }
        syncAll();
        commit();
      });
  }

  /* Forget the offer and everything on screen that describes it. */
  /* Showing the times, or showing the options. One switch, applied to every
   * root, because the cart page has two pickers on screen at once and they
   * must not disagree about which half of this is visible. */
  function showTimes(on) {
    roots().forEach(function (root) {
      if (root && root.classList) root.classList.toggle('is-picking-time', !!on);
    });
  }

  function clearOffer(root, answer, wrap) {
    showTimes(false);
    state.samedayZone = '';
    state.samedayLabel = '';
    state.samedayTier = '';
    state.samedayDeadline = '';
    state.samedayPricePence = '';
    if (answer) { answer.hidden = true; answer.textContent = ''; }
    if (wrap) wrap.hidden = true;
    var grid = $one('[data-ibc-fulfil-window-grid]', root);
    if (grid) grid.innerHTML = '';
    var notice = $one('[data-ibc-fulfil-cake-notice]', root);
    if (notice) { notice.hidden = true; notice.textContent = ''; }
  }

  /* The pending state. Four quotes to a courier take a moment and a basket
   * that looks broken for that moment is a basket somebody abandons. */
  function setQuoting(root, answer, on) {
    if (root) root.classList.toggle('is-quoting', !!on);
    if (on && answer) {
      answer.hidden = false;
      answer.className = 'ibc-fulfil__pc-answer is-waiting';
      /* A SPINNER, NOT JUST WORDS. Four courier quotes take a couple of
       * seconds, and a line of static text reads as a finished answer —
       * people conclude it is broken and click again. The dot is CSS. */
      answer.textContent = '';
      var spin = document.createElement('span');
      spin.className = 'ibc-fulfil__spinner';
      spin.setAttribute('aria-hidden', 'true');
      answer.appendChild(spin);
      answer.appendChild(document.createTextNode('Checking courier prices\u2026'));
    }
  }

  function tierById(tiers, id) {
    var found = null;
    (tiers || []).forEach(function (t) { if (t.id === id) found = t; });
    return found;
  }

  /* THE HEADLINE. The cheapest tier, named as a starting price, because the
   * first thing a customer wants to know is whether this is a five-pound
   * question or a twenty-five-pound one. */
  function offerLine(raw, data) {
    var cheapest = null;
    (data.tiers || []).forEach(function (t) {
      if (t.price_pence != null && (!cheapest || t.price_pence < cheapest.price_pence)) cheapest = t;
    });
    return 'We can reach ' + outwardCode(raw) + ' today' +
      (cheapest ? ' \u2014 from ' + cheapest.price_label : '') + '.';
  }

  /* Why there is nothing to offer, in the shop's own words where it has
   * them. A customer told "no" deserves to know which no it is. */
  function quoteMessage(data, cfg) {
    var reason = (data && data.reason) || '';
    if (reason === 'out_of_area') return (data && data.message) || cfg.samedayOut;
    if (reason === 'past_cutoff') {
      return 'Same-day orders have closed for today. Choose a delivery date instead.';
    }
    if (reason === 'cake_too_late') {
      return 'A whole cake needs longer than today has left. Choose a delivery date instead.';
    }
    if (reason === 'too_late_today' || reason === 'no_window') {
      return 'There is not enough of the day left for a courier. Choose a delivery date instead.';
    }
    if (reason === 'no_courier_prices') {
      return 'No couriers are quoting for today right now. Choose a delivery date instead.';
    }
    /* PRICED, BUT NOT AT A PRICE THE SHOP WILL WEAR. A different sentence
     * from an outage, because it is a different thing: the courier answered,
     * and the answer was too dear for this address today. */
    if (reason === 'too_dear') {
      return 'We cannot reach that postcode today at a sensible price. ' +
        'Choose a delivery date instead and we will post it.';
    }
    if (reason === 'sameday_off') return cfg.samedayOut;
    return 'We cannot deliver there today. Choose a delivery date instead.';
  }

  /* THE BASKET, AS THE SERVER NEEDS IT.
   *
   * Variant ids and quantities only. Weights and whether anything is a whole
   * cake are read back from Shopify at the other end — a basket that could
   * talk its own weight down would quote as a small parcel and be collected
   * as a cake, which is a mistake this integration has already made once. */
  function quoteRequest(cfg, postcode) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; })
      .then(function (cart) {
        var items = (cart.items || []).map(function (item) {
          return { variant_id: item.variant_id, quantity: item.quantity };
        });
        return fetch(cfg.quoteUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ postcode: postcode, items: items })
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('quote_failed');
        return response.json();
      });
  }

  /* THE TILES. One per tier, each carrying its own deadline and price.
   *
   * The price is rendered as a second line rather than appended to the
   * label, because "By 1:00pm £16.95" reads as one thought and they are
   * two: what you get, and what it costs. */
  /* THE LAST ANSWER, KEPT.
   *
   * The grid is built in JavaScript, and the cart controller replaces this
   * whole region's HTML after any basket mutation — adding the delivery
   * charge, for one. Whatever is in the DOM is therefore temporary, and the
   * only durable copy of "what we offered for this postcode" is here. */
  var lastQuote = null;

  function renderTiers(root, data) {
    if (data) lastQuote = { postcode: state.samedayPostcode, data: data };
    var grid = $one('[data-ibc-fulfil-window-grid]', root);
    if (!grid) return;
    grid.innerHTML = '';

    (data.tiers || []).forEach(function (tier) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'ibc-fulfil__window ibc-fulfil__tier' +
        (state.samedayTier === tier.id ? ' is-chosen' : '');
      button.setAttribute('data-ibc-fulfil-window', tier.label);
      button.setAttribute('data-tier-id', tier.id);
      /* THE MACHINE TIMES, CARRIED ALONGSIDE THE HUMAN ONE. The label is
       * display text; Gophr needs an instant. Reading one back out of a
       * sentence written for people is how an integration fails in a new
       * locale on a quiet Sunday. */
      button.setAttribute('data-tier-deadline', tier.deadline_iso || '');
      button.setAttribute('data-tier-price', String(tier.price_pence == null ? '' : tier.price_pence));
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', state.samedayTier === tier.id ? 'true' : 'false');

      var when = document.createElement('span');
      when.className = 'ibc-fulfil__tier-when';
      when.textContent = tier.label;
      button.appendChild(when);

      if (tier.price_label) {
        var price = document.createElement('span');
        price.className = 'ibc-fulfil__tier-price';
        price.textContent = tier.price_label;
        button.appendChild(price);
      }

      grid.appendChild(button);
    });

    /* THE CAKE LINE. Shown only when a cake is actually in the basket, and
     * written by the server so the shop's wording lives in one place. A
     * customer whose fastest option vanished is owed the reason. */
    var notice = $one('[data-ibc-fulfil-cake-notice]', root);
    if (notice) {
      var text = data.cake_notice || '';
      notice.textContent = text;
      notice.hidden = !text;
    }
  }

  /* Minutes past local midnight on the picker's own `today`, as an ISO
   * instant carrying the shop's offset. Exactly the spelling the collection
   * slots already use, so an order's two kinds of time read the same way. */
  function instantAt(root, minutes) {
    var cfg = conf(root);
    var n = Number(minutes);
    if (!isFinite(n)) return '';
    var h = Math.floor(n / 60);
    var m = Math.round(n - h * 60);
    /* A 9pm day end is 1260, not 1440, so this does not normally wrap. It is
     * clamped rather than trusted because a mis-set day_end should produce a
     * refused booking, not a time on tomorrow. */
    if (h > 23) { h = 23; m = 59; }
    return toISO(cfg.today) + 'T' + pad(h) + ':' + pad(m) + ':00' + cfg.tz;
  }

  function chooseTier(root, tier) {
    if (!tier || !tier.label || !tier.deadline) return;
    state.method = 'delivery';
    state.option = 'sameday';
    state.samedayTier = tier.id || '';
    state.samedayLabel = tier.label;
    state.samedayDeadline = tier.deadline;
    state.samedayPricePence = tier.price || '';

    /* THE OLD TWO KEYS, STILL WRITTEN, STILL MEANING WHAT THEY MEANT.
     *
     * window_end is the deadline the courier is held to, which is exactly
     * what it always was. window_start is when the basket is ready to be
     * collected — the server worked it out from the preparation this
     * basket needs, and a cake's hour is already inside it. Everything
     * downstream that reads these two goes on working untouched. */
    state.samedayEnd = tier.deadline;
    state.samedayStart = state.samedayReadyAt || '';

    /* Same-day IS a dated delivery whose date is today, so it writes the
     * same two keys. The validation function refuses an order carrying one
     * without the other. */
    state.deliveryDate = toISO(conf(root).today);
    state.deliveryLabel = tier.label;
    stage = nextStage('window');
    syncAll();
    commit();
  }

  function chooseDate(root, kind, iso) {
    var date = parseISO(iso);
    if (!date) return;
    var cfg = conf(root);
    if (dayState(cfg, kind, date) !== 'ok') return;

    if (kind === 'pickup') {
      state.method = 'pickup';
      state.pickupDate = iso;
      /* A new day invalidates the slot: 6:30pm on Tuesday is not 6:30pm on
       * Wednesday, and carrying it over would book a time nobody picked. */
      state.pickupStart = '';
      state.pickupEnd = '';
      state.pickupLabel = '';
    } else {
      state.method = 'delivery';
      state.option = 'scheduled';
      state.deliveryDate = iso;
      state.deliveryLabel = labelShort(date);
    }
    stage = nextStage('date');
    syncAll();
    commit();
  }

  function chooseSlot(start, end, label) {
    state.method = 'pickup';
    state.pickupStart = start;
    state.pickupEnd = end;
    state.pickupLabel = label;
    stage = nextStage('slot');
    syncAll();
    commit();
  }

  /* ==================================================================== */
  /* EVENTS — all delegated. See rule 2.                                  */
  /* ==================================================================== */

  /* ==================================================================== */
  /* NOTHING THIS FILE DOES MAY BREAK THE BASKET                          */
  /*                                                                      */
  /* Every handler below is delegated from `document`, which means an     */
  /* exception thrown inside one aborts that handler halfway through —    */
  /* leaving the picker in a state no code intended, and taking the rest  */
  /* of the click with it. A basket is somebody's money; a picker that    */
  /* mis-renders is a nuisance, and a picker that throws while writing to */
  /* the cart is a lost order.                                            */
  /*                                                                      */
  /* So each one is wrapped. A failure is logged with its origin and then */
  /* swallowed: the customer keeps a working basket, the console keeps    */
  /* the stack trace, and every other listener on the page still runs.    */

  function guard(where, fn) {
    return function (event) {
      try {
        return fn(event);
      } catch (error) {
        try {
          console.error('[ibc-fulfil] ' + where + ' failed', error);
        } catch (e) { /* a console that throws is not worth a second try */ }
        return undefined;
      }
    };
  }

  document.addEventListener('click', guard('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* COLLAPSE. A view, not an undo: the cart attributes are untouched and
     * the summary line stays on screen, so a collapsed picker still says
     * what was chosen and checkout stays live. */
    var collapse = closest(target, '[data-ibc-fulfil-collapse]');
    if (collapse) {
      event.preventDefault();
      setStage(stage === 'closed' ? resumeStage() : 'closed');
      return;
    }

    /* THE SETTLED BAR REOPENS THE PICKER. Deliberately `resumeStage()` rather
     * than 'options': a customer who taps a bar reading "Collect, Sat 20th,
     * 5:00pm" is going back to a finished choice, so the right place to land
     * is the review — with the date and the time there and each editable —
     * and not the method tiles, which would make them walk the whole flow
     * again to change a time. */
    var bar = closest(target, '[data-ibc-fulfil-bar]');
    if (bar) {
      event.preventDefault();
      setStage(resumeStage());
      return;
    }

    /* BACK, and CHANGE from the review. Same mechanism, different word: one
     * walks the flow backwards, the other jumps to the step that owns a
     * particular line. Neither touches the cart. */
    var back = closest(target, '[data-ibc-fulfil-back]');
    if (back) {
      event.preventDefault();
      setStage(back.getAttribute('data-ibc-fulfil-back'));
      return;
    }

    var edit = closest(target, '[data-ibc-fulfil-edit]');
    if (edit) {
      event.preventDefault();
      var to = edit.getAttribute('data-ibc-fulfil-edit');
      /* "Change" on the collapsed summary means "show me the review again",
       * which is the one edit target that is not itself a step. */
      if (to === 'review') {
        setStage('review');
      } else {
        setStage(to);
      }
      return;
    }

    var tab = closest(target, '[data-ibc-fulfil-tab]');
    if (tab) {
      event.preventDefault();
      chooseMethod(tab.getAttribute('data-ibc-fulfil-tab'));
      var root = closest(tab, '[data-ibc-fulfil]');
      var again = root && $one('[data-ibc-fulfil-tab="' + tab.getAttribute('data-ibc-fulfil-tab') + '"]', root);
      if (again) again.focus();
      return;
    }

    var nav = closest(target, '[data-ibc-cal-nav]');
    if (nav) {
      event.preventDefault();
      var navWrap = closest(nav, '[data-ibc-cal]');
      var navRoot = closest(nav, '[data-ibc-fulfil]');
      if (!navWrap || !navRoot) return;
      var kind = navWrap.getAttribute('data-ibc-cal');
      var shown = monthShown(navRoot, kind) || openMonth(conf(navRoot), kind);
      var step = Number(nav.getAttribute('data-ibc-cal-nav')) || 0;
      var moved = new Date(shown.getFullYear(), shown.getMonth() + step, 1, 12, 0, 0, 0);
      /* Both surfaces page together, so the drawer and the page never show
       * different months of the same calendar. */
      roots().forEach(function (each) {
        renderCalendar(each, kind, moved);
      });
      return;
    }

    var day = closest(target, '[data-ibc-cal-day]');
    if (day && day.getAttribute('aria-disabled') !== 'true') {
      event.preventDefault();
      var dayWrap = closest(day, '[data-ibc-cal]');
      var dayRoot = closest(day, '[data-ibc-fulfil]');
      if (!dayWrap || !dayRoot) return;
      chooseDate(dayRoot, dayWrap.getAttribute('data-ibc-cal'), day.getAttribute('data-ibc-cal-day'));
      return;
    }

    var slot = closest(target, '[data-ibc-fulfil-slot]');
    if (slot) {
      event.preventDefault();
      chooseSlot(
        slot.getAttribute('data-ibc-fulfil-slot'),
        slot.getAttribute('data-end'),
        slot.getAttribute('data-label')
      );
      return;
    }

    var why = closest(target, '[data-ibc-fulfil-why]');
    if (why) {
      event.preventDefault();
      var whyRoot = closest(why, '[data-ibc-fulfil]');
      var body = whyRoot && $one('[data-ibc-fulfil-why-body]', whyRoot);
      if (!body) return;
      var opened = why.getAttribute('aria-expanded') === 'true';
      why.setAttribute('aria-expanded', opened ? 'false' : 'true');
      body.hidden = opened;
    }
  }));

  document.addEventListener('change', guard('change', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-ibc-fulfil-option')) return;
    chooseOption(input.value);
  }));

  /* ---- same-day: the postcode and the window ----

     CHECKED ON BLUR AND ON ENTER, not on every keystroke. A partial postcode
     is not a wrong one, and telling somebody "we cannot reach W1" while they
     are still typing "W1T 1JG" is both untrue and irritating. */
  document.addEventListener('click', guard('click', function (event) {
    var go = closest(event.target, '[data-ibc-fulfil-postcode-go]');
    if (go) {
      var root = closest(go, '[data-ibc-fulfil]');
      if (root) checkPostcode(root);
      return;
    }
    var back = closest(event.target, '[data-ibc-fulfil-show-options]');
    if (back) {
      /* BACK MEANS BACK TO THE START, not a peek at the other options.
       *
       * Keeping the prices on screen behind the fold sounded kinder and was
       * worse: the customer returns to a basket still holding a courier
       * charge for a time they may no longer want, quoted minutes ago at a
       * price that moves. Clearing it is the honest reset — and clearSameday
       * takes the charge out of the basket with it, so nothing is being paid
       * for a choice that has been withdrawn. */
      var backRoot = closest(back, '[data-ibc-fulfil]');
      lastQuote = null;
      clearSameday();
      if (backRoot) {
        var pc = $one('[data-ibc-fulfil-postcode]', backRoot);
        if (pc) pc.value = '';
        clearOffer(
          backRoot,
          $one('[data-ibc-fulfil-postcode-answer]', backRoot),
          $one('[data-ibc-fulfil-windows]', backRoot)
        );
      }
      /* Named explicitly rather than routed through nextStage(), which
       * answers `stage` unchanged for a step it does not recognise — a quiet
       * no-op that would have looked like it worked. On the cart page the
       * sentinel 'all' means every step is on screen and must be left be. */
      if (stage !== 'all') stage = 'options';
      syncAll();
      commit();
      return;
    }

    var win = closest(event.target, '[data-ibc-fulfil-window]');
    if (win) {
      var winRoot = closest(win, '[data-ibc-fulfil]');
      if (winRoot) {
        chooseTier(winRoot, {
          id: win.getAttribute('data-tier-id'),
          label: win.getAttribute('data-ibc-fulfil-window'),
          deadline: win.getAttribute('data-tier-deadline'),
          price: win.getAttribute('data-tier-price')
        });
      }
    }
  }));

  document.addEventListener('blur', guard('blur', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-ibc-fulfil-postcode')) return;
    var root = closest(input, '[data-ibc-fulfil]');
    if (root) checkPostcode(root);
  }), true);

  document.addEventListener('keydown', guard('keydown', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-ibc-fulfil-postcode')) return;
    if (event.key !== 'Enter') return;
    /* A postcode field inside a form would otherwise submit it. */
    event.preventDefault();
    var root = closest(input, '[data-ibc-fulfil]');
    if (root) checkPostcode(root);
  }));

  /* Keyboard: the tab strip, and the grid. Both follow the usual pattern —
   * arrows move, and moving a roving focus is not the same as choosing. */
  document.addEventListener('keydown', guard('keydown', function (event) {
    var key = event.key;

    var tab = closest(event.target, '[data-ibc-fulfil-tab]');
    if (tab && (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End')) {
      var strip = closest(tab, '.ibc-fulfil__tabs');
      if (!strip) return;
      var tabs = $all('[data-ibc-fulfil-tab]', strip);
      var at = tabs.indexOf(tab);
      var to = at;
      if (key === 'ArrowLeft') to = (at - 1 + tabs.length) % tabs.length;
      if (key === 'ArrowRight') to = (at + 1) % tabs.length;
      if (key === 'Home') to = 0;
      if (key === 'End') to = tabs.length - 1;
      event.preventDefault();
      tabs[to].focus();
      chooseMethod(tabs[to].getAttribute('data-ibc-fulfil-tab'));
      return;
    }

    var day = closest(event.target, '[data-ibc-cal-day]');
    if (!day) return;

    var from = parseISO(day.getAttribute('data-ibc-cal-day'));
    if (!from) return;
    var column = (from.getDay() + 6) % 7;

    var step = 0;
    if (key === 'ArrowLeft') step = -1;
    else if (key === 'ArrowRight') step = 1;
    else if (key === 'ArrowUp') step = -7;
    else if (key === 'ArrowDown') step = 7;
    else if (key === 'Home') step = -column;
    else if (key === 'End') step = 6 - column;
    else if (key === 'PageUp') step = -28;
    else if (key === 'PageDown') step = 28;
    else return;

    event.preventDefault();

    var wrap = closest(day, '[data-ibc-cal]');
    var root = closest(day, '[data-ibc-fulfil]');
    if (!wrap || !root) return;
    var kind = wrap.getAttribute('data-ibc-cal');
    var to = addDays(from, step);
    var cfg = conf(root);

    /* Do not walk out of the offerable range: clamp instead, so holding an
     * arrow key cannot strand focus in 2031. */
    if (to < minDate(cfg, kind)) to = minDate(cfg, kind);
    if (to > maxDate(cfg)) to = maxDate(cfg);

    var targetISO = toISO(to);
    var monthStart = new Date(to.getFullYear(), to.getMonth(), 1, 12, 0, 0, 0);
    var shown = monthShown(root, kind);
    if (!shown || shown.getMonth() !== monthStart.getMonth() || shown.getFullYear() !== monthStart.getFullYear()) {
      roots().forEach(function (each) {
        renderCalendar(each, kind, monthStart);
      });
    }

    var next = $one('[data-ibc-cal="' + kind + '"] [data-ibc-cal-day="' + targetISO + '"]', root);
    if (next) {
      $all('[data-ibc-cal="' + kind + '"] [data-ibc-cal-day]', root).forEach(function (each) {
        each.setAttribute('tabindex', '-1');
      });
      next.setAttribute('tabindex', '0');
      next.focus();
    }
  }));

  /* ==================================================================== */
  /* SURVIVING THE SECTION SWAP                                           */
  /* ==================================================================== */

  /* A SCOPED observer, not one over document.body.
   *
   * `assets/ibc-cart.js` re-renders by assigning innerHTML on the two
   * `[data-ibc-cart-refresh]` hosts. Those hosts are never themselves
   * replaced, so one childList observer per host — two nodes on /cart, one
   * everywhere else — sees every swap and nothing else on the page.
   *
   * The event alternative does not work in both directions: `ibc:cart-changed`
   * fires AFTER the swap when the cart itself mutated, but BEFORE it when the
   * product page raised it, because ibc-cart.js starts its own async refresh
   * from that same listener. Watching the node is true in both cases. */
  var observer = null;

  function watch() {
    if (typeof MutationObserver !== 'function') return;
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      seed();
      syncAll();
    });
    $all('[data-ibc-cart-refresh]').forEach(function (host) {
      observer.observe(host, { childList: true });
    });
  }

  function start() {
    if (!roots().length) {
      /* Nothing to do on this page, but the basket may become non-empty via
       * the drawer, so the hosts are still watched. */
      watch();
      return;
    }
    seed();
    syncAll();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.IbcFulfilment = {
    sync: syncAll,
    stage: function () { return stage; },
    setStage: setStage,
    state: function () {
      var copy = {};
      Object.keys(state).forEach(function (key) { copy[key] = state[key]; });
      return copy;
    },
    ready: ready,
    attributes: attributesFor,
    slotsFor: slotsFor,
    dayState: dayState,
    /* Exposed for the harness, which has to be able to ask what the earliest
     * offered date is on a given day without waiting for that day. */
    minDate: minDate,
    conf: conf
  };
})();
